import {
  useCallback, useEffect, useMemo, useRef,
} from 'react';
import moment from 'moment-timezone';
import type Specialist from '../model/Specialist';
import type AvailableTime from '../model/AvailableTime';
import { AppointmentType } from '../model';
import { availabilitiesApi } from '../services/availabilities';
import { useAppDispatch, useAppSelector } from './redux-hooks';
import useNow from './useNow';
import {
  BookableDay, getBookableDays, getWindowEnd, getWindowMonths,
} from '../utils/clinician-availability';
import type { RootState } from '../redux/store';

export interface ClinicianAvailability {
  isLoading: boolean;
  // Bookable days inside the three-month "next available" window.
  days: BookableDay[];
  // A month could not be loaded and nothing was found, so "no times" is not known to be true.
  hasError: boolean;
}

const LOADING: ClinicianAvailability = { isLoading: true, days: [], hasError: false };

const { getAvailabilitiesBySpecialist } = availabilitiesApi.endpoints;

const queryArgs = (specialistId: string, month: string, timezone: string) => ({
  specialistId, month, timezone, appointmentType: AppointmentType.NEW,
});
const keyOf = (specialistId: string, month: string, timezone: string) => `${specialistId}|${month}|${timezone}`;

// Finds each clinician's bookable days in the coming three months. Like the ADHD flow, months are
// asked for one at a time and the next is only fetched while nothing has been found yet.
// Requests stay subscribed while the caller is mounted and are released on unmount.
const useClinicianAvailability = (specialists: Specialist[] | undefined, timezone: string) => {
  const dispatch = useAppDispatch();
  const apiState = useAppSelector((state) => state[availabilitiesApi.reducerPath]);

  // The window is anchored when the page opens so it does not shift under the patient, while
  // `now` keeps moving so times that have started drop out.
  const opened = useMemo(() => moment(), []);
  const now = useNow();
  const windowEnd = useMemo(() => getWindowEnd(opened, timezone), [opened, timezone]);
  const months = useMemo(() => getWindowMonths(opened, timezone), [opened, timezone]);

  const { byId, wanted } = useMemo(() => {
    const state = { [availabilitiesApi.reducerPath]: apiState } as RootState;
    const keys: string[] = [];
    const result: Record<string, ClinicianAvailability> = {};

    (specialists ?? []).forEach((specialist) => {
      const loaded: AvailableTime[] = [];
      let found: BookableDay[] = [];
      let isLoading = false;
      let failed = false;

      months.every((month) => {
        keys.push(keyOf(specialist.id, month, timezone));
        const query = getAvailabilitiesBySpecialist.select(queryArgs(specialist.id, month, timezone))(state);
        if (query.isUninitialized || query.isLoading) {
          isLoading = true;
          return false;
        }
        // A failed month is remembered, and the search moves on to the next.
        failed = failed || query.isError;
        loaded.push(...((query.data ?? []) as AvailableTime[]));
        found = getBookableDays(loaded, specialist.appointmentLength, now, timezone, windowEnd);
        return found.length === 0;
      });

      result[specialist.id] = { isLoading, days: isLoading ? [] : found, hasError: !isLoading && failed && !found.length };
    });

    return { byId: result, wanted: keys.sort().join(',') };
  }, [apiState, specialists, months, now, windowEnd, timezone]);

  const subscriptions = useRef(new Map<string, { unsubscribe:() => void }>());

  useEffect(() => {
    const keys = new Set(wanted ? wanted.split(',') : []);
    keys.forEach((key) => {
      if (!subscriptions.current.has(key)) {
        const [specialistId, month, zone] = key.split('|');
        subscriptions.current.set(key, dispatch(getAvailabilitiesBySpecialist.initiate(queryArgs(specialistId, month, zone))));
      }
    });
    subscriptions.current.forEach((subscription, key) => {
      if (!keys.has(key)) {
        subscription.unsubscribe();
        subscriptions.current.delete(key);
      }
    });
  }, [dispatch, wanted]);

  useEffect(() => {
    const { current } = subscriptions;
    return () => {
      current.forEach((subscription) => subscription.unsubscribe());
      current.clear();
    };
  }, []);

  const getAvailability = useCallback((id: string) => byId[id] ?? LOADING, [byId]);
  const isAnyLoading = Object.values(byId).some(({ isLoading }) => isLoading);
  const hasAnyError = Object.values(byId).some(({ hasError }) => hasError);

  return {
    getAvailability, isAnyLoading, hasAnyError, now,
  };
};

export default useClinicianAvailability;
